import { z } from 'zod';

/**
 * The provider events a webhook accepts into `payment_events`. A checkout session completing,
 * expiring, or settling an asynchronous payment either way, and a charge refunded. Sessions are card
 * only, so the asynchronous two are subscribed to in case a payment ever settles late, and each is
 * reconciled from the session's current state like the rest. Every other type is answered and not
 * stored.
 */
export const PAYMENT_WEBHOOK_EVENT_TYPES = [
  'checkout.session.completed',
  'checkout.session.async_payment_succeeded',
  'checkout.session.async_payment_failed',
  'checkout.session.expired',
  'charge.refunded',
] as const;
export type PaymentWebhookEventType = (typeof PAYMENT_WEBHOOK_EVENT_TYPES)[number];

/**
 * One accepted provider event, handed to the worker to process. The job carries the
 * `payment_events` row's id, and a job id equal to it is what makes a redelivered event, which
 * inserts no row, send no second job. A refund's job also carries the payment its charge was for, as
 * the verified event named it, so processing it needs no read of the charge; the sweep, which has no
 * job to read it from, asks the provider instead.
 */
export const QUEUE_PAYMENT_EVENT = 'payment.event';

export const paymentEventPayloadSchema = z.object({
  eventId: z.uuid(),
  paymentIntentId: z.string().min(1).optional(),
});
export type PaymentEventPayload = z.infer<typeof paymentEventPayloadSchema>;

/**
 * Where pg-boss moves a `payment.event` job that ran out of attempts, or failed without its handler
 * finishing. Its handler logs the event at `error` and leaves it unprocessed, so the sweep processes
 * it once its job is gone. The payload is the event job's own.
 */
export const QUEUE_PAYMENT_EVENT_DEAD_LETTER = 'payment.event.dead-letter';

/**
 * The reconciliation sweep, run on a schedule and once when a worker starts. It is what settles a
 * purchase whose webhook never arrived, an event whose job was lost, and an attempt that never
 * opened a session. Its payload is empty.
 */
export const QUEUE_PAYMENT_RECONCILE = 'payment.reconcile';
